import { useState, useEffect } from 'react';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
  timeout?: number; // Timeout in milliseconds (default: 10000ms = 10s)
  onRetry?: () => void; // Callback when user clicks retry button
}

const LoadingSpinner = ({ size = 'medium', message, timeout = 10000, onRetry }: LoadingSpinnerProps) => {
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (!timeout) return;

    const timer = setTimeout(() => {
      setHasTimedOut(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  };

  const spinnerStyle: React.CSSProperties = {
    width: size === 'small' ? '16px' : size === 'large' ? '48px' : '32px',
    height: size === 'small' ? '16px' : size === 'large' ? '48px' : '32px',
  };

  const messageStyle: React.CSSProperties = {
    marginTop: '1rem',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    color: 'var(--text-primary)',
  };

  const errorMessageStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: '500',
    marginBottom: '0.5rem',
  };

  const errorDescriptionStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginBottom: '1rem',
  };

  const retryButtonStyle: React.CSSProperties = {
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    padding: '0.5rem 1.5rem',
    borderRadius: 'var(--radius)',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    minHeight: '40px',
  };

  // Show timeout error state
  if (hasTimedOut) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          <div style={errorMessageStyle}>Taking longer than expected</div>
          <div style={errorDescriptionStyle}>The server is not responding. Please check your connection.</div>
          {onRetry && (
            <button
              onClick={onRetry}
              style={retryButtonStyle}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show loading state
  return (
    <div style={containerStyle}>
      <div className="loading-spinner" style={spinnerStyle}></div>
      {message && <p style={messageStyle}>{message}</p>}
    </div>
  );
};

export default LoadingSpinner;
